import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CreateTenantAction } from '@/application/actions';
import { useToast } from '@/presentation/contexts/ToastContext';
import { usePlans } from '@/presentation/hooks/usePlans';
import { Button, Input } from '@/presentation/components/common';
import type { TenantFormData } from '@/domain/repositories/ITenantRepository';
import { TIMEZONES } from '@/shared/constants/timezones';
import { isRetiredIntermedioPlan } from '@/domain/types/planCatalog';

interface CreateTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function phoneDigits(value: string): string {
  return value.trim().replace(/[\s\-()']/g, '');
}

export const CreateTenantModal: React.FC<CreateTenantModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { success, error: showError, warning } = useToast();
  const { plans } = usePlans();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<TenantFormData>({
    nombre: '',
    primerNombre: '',
    apellido: '',
    emailAdministrador: '',
    telefono: '',
    nombreConsultorio: '',
    direccionConsultorio: '',
    telefonoConsultorio: '',
    timeZoneId: 'America/Argentina/Buenos_Aires',
    fechaVencimiento: undefined,
    planAdminId: undefined,
  });

  const availableTimezones = TIMEZONES;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const primerNombre = formData.primerNombre?.trim() ?? '';
    const apellido = formData.apellido?.trim() ?? '';
    const email = formData.emailAdministrador.trim();
    const telefono = formData.telefono?.trim() ?? '';
    const clinicName = formData.nombre.trim();
    const officeName = formData.nombreConsultorio?.trim() ?? '';
    const officeAddress = formData.direccionConsultorio?.trim() ?? '';
    const officePhone = formData.telefonoConsultorio?.trim() ?? '';

    if (primerNombre.length < 2) {
      showError(t('pages.tenants.validation.firstNameRequired'));
      return;
    }
    if (apellido.length < 2) {
      showError(t('pages.tenants.validation.lastNameRequired'));
      return;
    }
    if (!email) {
      showError(t('pages.tenants.validation.emailRequired'));
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      showError(t('pages.tenants.validation.emailInvalid'));
      return;
    }
    if (phoneDigits(telefono).replace(/^\+/, '').length < 8) {
      showError(t('pages.tenants.validation.phoneRequired'));
      return;
    }
    if (clinicName.length < 3) {
      showError(t('pages.tenants.validation.nameRequired'));
      return;
    }
    if (officeName.length < 3) {
      showError(t('pages.tenants.validation.officeNameRequired'));
      return;
    }
    if (officeAddress.length < 5) {
      showError(t('pages.tenants.validation.officeAddressRequired'));
      return;
    }
    if (phoneDigits(officePhone).replace(/^\+/, '').length < 8) {
      showError(t('pages.tenants.validation.officePhoneRequired'));
      return;
    }

    setIsLoading(true);
    try {
      const action = container.get<CreateTenantAction>(TYPES.CreateTenantAction);
      const result = await action.execute({
        ...formData,
        primerNombre,
        apellido,
        emailAdministrador: email,
        telefono,
        nombre: clinicName,
        nombreConsultorio: officeName,
        direccionConsultorio: officeAddress,
        telefonoConsultorio: officePhone,
      });
      if (result.emailWarning) {
        warning(result.emailWarning, 8000);
      } else if (result.linkedExistingAccount) {
        success(t('pages.tenants.createdSuccessLinked', { email }));
      } else {
        success(t('pages.tenants.createdSuccess', { email }));
      }
      onSuccess();
    } catch (err) {
      const axiosErr = err as { code?: string; message?: string; response?: { data?: { message?: string } } };
      const timedOut = axiosErr.code === 'ECONNABORTED' || /timeout/i.test(axiosErr.message ?? '');
      const msg = axiosErr.response?.data?.message;
      showError(
        msg ||
          (timedOut
            ? t('pages.tenants.errorCreatingTimeout')
            : err instanceof Error
              ? err.message
              : t('pages.tenants.errorCreating'))
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={isLoading ? undefined : onClose}></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {t('pages.tenants.create')}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isLoading}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('pages.tenants.createSubtitle')}
              </p>

              <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
                <section>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    {t('pages.solicitudes.sections.personalData')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label={t('pages.solicitudes.fields.firstName')}
                      type="text"
                      value={formData.primerNombre ?? ''}
                      onChange={(e) => setFormData({ ...formData, primerNombre: e.target.value })}
                      required
                    />
                    <Input
                      label={t('pages.solicitudes.fields.lastName')}
                      type="text"
                      value={formData.apellido ?? ''}
                      onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                      required
                    />
                    <Input
                      label={t('pages.solicitudes.fields.email')}
                      type="email"
                      value={formData.emailAdministrador}
                      onChange={(e) => setFormData({ ...formData, emailAdministrador: e.target.value })}
                      required
                    />
                    <Input
                      label={t('pages.solicitudes.fields.phone')}
                      type="tel"
                      value={formData.telefono ?? ''}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      required
                    />
                  </div>
                </section>

                <section>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    {t('pages.solicitudes.sections.clinicData')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label={t('pages.solicitudes.fields.clinicName')}
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('pages.solicitudes.fields.timezone')}
                      </label>
                      <select
                        value={formData.timeZoneId}
                        onChange={(e) => setFormData({ ...formData, timeZoneId: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      >
                        {availableTimezones.map((tz) => (
                          <option key={tz} value={tz}>
                            {tz === formData.timeZoneId ? `${tz} ✓` : tz}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>

                <section>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    {t('pages.solicitudes.sections.officeData')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label={t('pages.solicitudes.fields.officeName')}
                      type="text"
                      value={formData.nombreConsultorio ?? ''}
                      onChange={(e) => setFormData({ ...formData, nombreConsultorio: e.target.value })}
                      required
                    />
                    <Input
                      label={t('pages.solicitudes.fields.officePhone')}
                      type="tel"
                      value={formData.telefonoConsultorio ?? ''}
                      onChange={(e) => setFormData({ ...formData, telefonoConsultorio: e.target.value })}
                      required
                    />
                    <div className="sm:col-span-2">
                      <Input
                        label={t('pages.solicitudes.fields.officeAddress')}
                        type="text"
                        value={formData.direccionConsultorio ?? ''}
                        onChange={(e) => setFormData({ ...formData, direccionConsultorio: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </section>

                <section>
                  <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                    {t('pages.tenants.sections.commercial')}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label={t('pages.tenants.expirationDate')}
                      type="date"
                      value={formData.fechaVencimiento ? new Date(formData.fechaVencimiento).toISOString().split('T')[0] : ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        fechaVencimiento: e.target.value ? new Date(e.target.value) : undefined,
                      })}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('pages.tenants.plan')} — SaaS (Base / Avanzado)
                      </label>
                      <select
                        value={formData.planAdminId || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          planAdminId: e.target.value ? parseInt(e.target.value, 10) : undefined,
                        })}
                        className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">{t('pages.tenants.selectPlan')}</option>
                        {plans.filter((p) => p.isActive() && !isRetiredIntermedioPlan(p)).map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.nombre}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Coofit Conexión (plan especial) y packs de mensajería se asignan después, en Editar clínica.
                      </p>
                    </div>
                  </div>
                </section>

                {isLoading && (
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {t('pages.solicitudes.approveModal.processingHint')}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-slate-50 dark:bg-gray-900/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <Button
                type="submit"
                variant="primary"
                isLoading={isLoading}
                className="w-full sm:w-auto sm:ml-3"
              >
                {t('pages.solicitudes.actions.approve')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
                className="mt-3 w-full sm:mt-0 sm:w-auto"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
